/**
 * Inventory API Service
 * Handles all API calls for the Inventory module including:
 * - DraggitRM (Resource Management)
 * - Product Management
 * - Warehouses & Storage
 * - Shipping & Receiving
 */

import { apiRequest } from './apiClient';

class InventoryAPI {
  constructor() {
    // no instance state needed
  }

  // ==================== DRAGGIT RM - RESOURCE ENDPOINTS ====================

  async getResources(organization, type = null) {
    const params = new URLSearchParams({ organization });
    if (type) params.append('type', type);
    return apiRequest(`/resources?${params}`);
  }

  async createResource(resourceData) {
    return apiRequest('/resources', {
      method: 'POST',
      body: resourceData,
    });
  }

  async updateResource(resourceId, updates) {
    return apiRequest(`/resources/${resourceId}`, {
      method: 'PUT',
      body: updates,
    });
  }

  async deleteResource(resourceId) {
    return apiRequest(`/resources/${resourceId}`, {
      method: 'DELETE',
    });
  }

  // ==================== DRAGGIT RM - PROJECT ENDPOINTS ====================

  async getProjects(organization) {
    return apiRequest(`/projects?organization=${organization}`);
  }

  async createProject(projectData) {
    return apiRequest('/projects', {
      method: 'POST',
      body: projectData,
    });
  }

  async updateProject(projectId, updates) {
    return apiRequest(`/projects/${projectId}`, {
      method: 'PUT',
      body: updates,
    });
  }

  async deleteProject(projectId) {
    return apiRequest(`/projects/${projectId}`, {
      method: 'DELETE',
    });
  }

  // ==================== DRAGGIT RM - TASK ENDPOINTS ====================

  async getTasks(filters = {}) {
    const params = new URLSearchParams();
    if (filters.projectId) params.append('projectId', filters.projectId);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.organization) params.append('organization', filters.organization);

    return apiRequest(`/tasks?${params}`);
  }

  async createTask(taskData) {
    return apiRequest('/tasks', {
      method: 'POST',
      body: taskData,
    });
  }

  async updateTask(taskId, updates) {
    return apiRequest(`/tasks/${taskId}`, {
      method: 'PUT',
      body: updates,
    });
  }

  async deleteTask(taskId) {
    return apiRequest(`/tasks/${taskId}`, {
      method: 'DELETE',
    });
  }

  // ==================== DRAGGIT RM - ASSIGNMENT ENDPOINTS ====================

  async assignResource(resourceId, taskId, quantity = 1.0) {
    return apiRequest('/assignments', {
      method: 'POST',
      body: { resourceId, taskId, quantity },
    });
  }

  async unassignResource(assignmentId) {
    return apiRequest(`/assignments/${assignmentId}`, {
      method: 'DELETE',
    });
  }

  // ==================== PRODUCT MANAGEMENT ENDPOINTS ====================

  async getProducts(organization, filters = {}) {
    const params = new URLSearchParams({ organization });
    if (filters.categoryId) params.append('categoryId', filters.categoryId);
    if (filters.type) params.append('type', filters.type);

    return apiRequest(`/products?${params}`);
  }

  async createProduct(productData) {
    return apiRequest('/products', {
      method: 'POST',
      body: productData,
    });
  }

  async updateProduct(productId, updates) {
    return apiRequest(`/products/${productId}`, {
      method: 'PUT',
      body: updates,
    });
  }

  async deleteProduct(productId) {
    return apiRequest(`/products/${productId}`, {
      method: 'DELETE',
    });
  }

  async importProducts(organization, products) {
    // No backend bulk endpoint — processed client-side per product in useImportProducts hook
    throw new Error('Use useImportProducts hook instead — processes via createProduct');
  }

  // ==================== WAREHOUSE ENDPOINTS ====================

  async getWarehouses(organization) {
    return apiRequest(`/warehouses?organization=${organization}`);
  }

  async createWarehouse(warehouseData) {
    return apiRequest('/warehouses', {
      method: 'POST',
      body: warehouseData,
    });
  }

  async updateWarehouse(warehouseId, updates) {
    return apiRequest(`/warehouses/${warehouseId}`, {
      method: 'PUT',
      body: updates,
    });
  }

  async deleteWarehouse(warehouseId) {
    return apiRequest(`/warehouses/${warehouseId}`, {
      method: 'DELETE',
    });
  }

  // ==================== STOCK MOVEMENT ENDPOINTS ====================

  async getStockMovements(organization, filters = {}) {
    const params = new URLSearchParams({ organization });
    if (filters.productId) params.append('productId', filters.productId);
    if (filters.warehouseId) params.append('warehouseId', filters.warehouseId);
    if (filters.type) params.append('type', filters.type);

    return apiRequest(`/stock-movements?${params}`);
  }

  async createStockMovement(movementData) {
    return apiRequest('/stock-movements', {
      method: 'POST',
      body: movementData,
    });
  }

  async updateStockMovement(movementId, updates) {
    return apiRequest(`/stock-movements/${movementId}`, {
      method: 'PUT',
      body: updates,
    });
  }

  // ==================== SHIPMENT ENDPOINTS ====================

  async getShipments(organization, status = null) {
    const params = new URLSearchParams({ organization });
    if (status) params.append('status', status);

    return apiRequest(`/shipments?${params}`);
  }

  async createShipment(shipmentData) {
    return apiRequest('/shipments', {
      method: 'POST',
      body: shipmentData,
    });
  }

  async updateShipment(shipmentId, updates) {
    return apiRequest(`/shipments/${shipmentId}`, {
      method: 'PUT',
      body: updates,
    });
  }

  // ==================== RECEIPT ENDPOINTS ====================

  async getReceipts(organization, status = null) {
    const params = new URLSearchParams({ organization });
    if (status) params.append('status', status);

    return apiRequest(`/receipts?${params}`);
  }

  async createReceipt(receiptData) {
    return apiRequest('/receipts', {
      method: 'POST',
      body: receiptData,
    });
  }

  async updateReceipt(receiptId, updates) {
    return apiRequest(`/receipts/${receiptId}`, {
      method: 'PUT',
      body: updates,
    });
  }

  // ==================== SUPPLIER ENDPOINTS ====================

  async getSuppliers(organization) {
    return apiRequest(`/suppliers?organization=${organization}`);
  }

  async createSupplier(supplierData) {
    return apiRequest('/suppliers', {
      method: 'POST',
      body: supplierData,
    });
  }

  async updateSupplier(supplierId, updates) {
    return apiRequest(`/suppliers/${supplierId}`, {
      method: 'PUT',
      body: updates,
    });
  }

  async deleteSupplier(supplierId) {
    return apiRequest(`/suppliers/${supplierId}`, {
      method: 'DELETE',
    });
  }

  // ==================== PURCHASE ORDER ENDPOINTS ====================

  async getPurchaseOrders(organization, status = null) {
    const params = new URLSearchParams({ organization });
    if (status) params.append('status', status);
    return apiRequest(`/purchase-orders?${params}`);
  }

  async createPurchaseOrder(poData) {
    return apiRequest('/purchase-orders', {
      method: 'POST',
      body: poData,
    });
  }

  async updatePurchaseOrder(poId, updates) {
    return apiRequest(`/purchase-orders/${poId}`, {
      method: 'PUT',
      body: updates,
    });
  }

  async approvePurchaseOrder(poId, approvedBy) {
    return apiRequest(`/purchase-orders/${poId}/approve`, {
      method: 'POST',
      body: { approvedBy },
    });
  }

  // ==================== REORDER RULE ENDPOINTS ====================

  async getReorderRules(organization, productId = null) {
    const params = new URLSearchParams({ organization });
    if (productId) params.append('productId', productId);
    return apiRequest(`/reorder-rules?${params}`);
  }

  async createReorderRule(ruleData) {
    return apiRequest('/reorder-rules', {
      method: 'POST',
      body: ruleData,
    });
  }

  async updateReorderRule(ruleId, updates) {
    return apiRequest(`/reorder-rules/${ruleId}`, {
      method: 'PUT',
      body: updates,
    });
  }

  async deleteReorderRule(ruleId) {
    return apiRequest(`/reorder-rules/${ruleId}`, {
      method: 'DELETE',
    });
  }

  // ==================== STOCK ALERT ENDPOINTS ====================

  async getStockAlerts(organization, filters = {}) {
    const params = new URLSearchParams({ organization });
    if (filters.status) params.append('status', filters.status);
    if (filters.severity) params.append('severity', filters.severity);
    return apiRequest(`/stock-alerts?${params}`);
  }

  async createStockAlert(alertData) {
    return apiRequest('/stock-alerts', {
      method: 'POST',
      body: alertData,
    });
  }

  async resolveStockAlert(alertId, resolvedBy) {
    return apiRequest(`/stock-alerts/${alertId}/resolve`, {
      method: 'POST',
      body: { resolvedBy },
    });
  }

  // ==================== INVENTORY ADJUSTMENT ENDPOINTS ====================

  async getInventoryAdjustments(organization, status = null) {
    const params = new URLSearchParams({ organization });
    if (status) params.append('status', status);
    return apiRequest(`/inventory-adjustments?${params}`);
  }

  async createInventoryAdjustment(adjustmentData) {
    return apiRequest('/inventory-adjustments', {
      method: 'POST',
      body: adjustmentData,
    });
  }

  async approveInventoryAdjustment(adjustmentId, approvedBy) {
    return apiRequest(`/inventory-adjustments/${adjustmentId}/approve`, {
      method: 'POST',
      body: { approvedBy },
    });
  }

  // ==================== STOCK TRANSFER ENDPOINTS ====================

  async getStockTransfers(organization, status = null) {
    const params = new URLSearchParams({ organization });
    if (status) params.append('status', status);
    return apiRequest(`/stock-transfers?${params}`);
  }

  async createStockTransfer(transferData) {
    return apiRequest('/stock-transfers', {
      method: 'POST',
      body: transferData,
    });
  }

  async updateStockTransferStatus(transferId, status, userId) {
    return apiRequest(`/stock-transfers/${transferId}/status`, {
      method: 'PUT',
      body: { status, userId },
    });
  }

  // ==================== CYCLE COUNT ENDPOINTS ====================

  async getCycleCounts(organization, status = null) {
    const params = new URLSearchParams({ organization });
    if (status) params.append('status', status);
    return apiRequest(`/cycle-counts?${params}`);
  }

  async createCycleCount(cycleCountData) {
    return apiRequest('/cycle-counts', {
      method: 'POST',
      body: cycleCountData,
    });
  }

  async submitCycleCount(cycleCountId, userId) {
    return apiRequest(`/cycle-counts/${cycleCountId}/submit`, {
      method: 'POST',
      body: { userId },
    });
  }

  async completeCycleCount(cycleCountId, userId, autoAdjust = true) {
    return apiRequest(`/cycle-counts/${cycleCountId}/complete`, {
      method: 'POST',
      body: { userId, autoAdjust },
    });
  }

  // ==================== INVENTORY VALUATION ENDPOINTS ====================

  async getInventoryValuations(organization, filters = {}) {
    const params = new URLSearchParams({ organization });
    if (filters.valuationDate) params.append('valuationDate', filters.valuationDate);
    return apiRequest(`/inventory-valuations?${params}`);
  }

  async createInventoryValuation(valuationData) {
    return apiRequest('/inventory-valuations', {
      method: 'POST',
      body: valuationData,
    });
  }

  // ==================== STORAGE LOCATION ENDPOINTS ====================

  async getStorageLocations(organization, warehouseId = null) {
    const params = new URLSearchParams({ organization });
    if (warehouseId) params.append('warehouseId', warehouseId);
    return apiRequest(`/storage-locations?${params}`);
  }

  async createStorageLocation(locationData) {
    return apiRequest('/storage-locations', {
      method: 'POST',
      body: locationData,
    });
  }

  async updateStorageLocation(locationId, updates) {
    return apiRequest(`/storage-locations/${locationId}`, {
      method: 'PUT',
      body: updates,
    });
  }

  // ==================== ENHANCED INVENTORY - CUSTOMER ENDPOINTS ====================

  async getCustomers(organization, type = null, activeOnly = true) {
    const params = new URLSearchParams({ organization, activeOnly: activeOnly.toString() });
    if (type) params.append('type', type);
    return apiRequest(`/customers?${params}`);
  }

  async createCustomer(customerData) {
    return apiRequest('/customers', {
      method: 'POST',
      body: customerData,
    });
  }

  async updateCustomer(customerId, updates) {
    return apiRequest(`/customers/${customerId}`, {
      method: 'PUT',
      body: updates,
    });
  }

  async deleteCustomer(customerId) {
    return apiRequest(`/customers/${customerId}`, {
      method: 'DELETE',
    });
  }

  // ==================== ENHANCED INVENTORY - SALES ORDER ENDPOINTS ====================

  async getSalesOrders(organization, customerId = null, status = null) {
    const params = new URLSearchParams({ organization });
    if (customerId) params.append('customerId', customerId);
    if (status) params.append('status', status);
    return apiRequest(`/sales-orders?${params}`);
  }

  async createSalesOrder(orderData) {
    return apiRequest('/sales-orders', {
      method: 'POST',
      body: orderData,
    });
  }

  async updateSalesOrder(orderId, updates) {
    return apiRequest(`/sales-orders/${orderId}`, {
      method: 'PUT',
      body: updates,
    });
  }

  async deleteSalesOrder(orderId) {
    return apiRequest(`/sales-orders/${orderId}`, {
      method: 'DELETE',
    });
  }

  async updateSalesOrderStatus(orderId, status) {
    return apiRequest(`/sales-orders/${orderId}/status`, {
      method: 'PUT',
      body: { status },
    });
  }

  // ==================== ENHANCED INVENTORY - PRODUCT STOCK ENDPOINTS ====================

  async getProductStock(organization, productId = null, warehouseId = null, locationId = null, status = null) {
    const params = new URLSearchParams({ organization });
    if (productId) params.append('productId', productId);
    if (warehouseId) params.append('warehouseId', warehouseId);
    if (locationId) params.append('locationId', locationId);
    if (status) params.append('status', status);
    return apiRequest(`/product-stock?${params}`);
  }

  async createProductStock(stockData) {
    return apiRequest('/product-stock', {
      method: 'POST',
      body: stockData,
    });
  }

  async updateProductStock(stockId, updates) {
    return apiRequest(`/product-stock/${stockId}`, {
      method: 'PUT',
      body: updates,
    });
  }

  async deleteProductStock(stockId) {
    return apiRequest(`/product-stock/${stockId}`, {
      method: 'DELETE',
    });
  }

  // ==================== ENHANCED INVENTORY - STOCK RESERVATION ENDPOINTS ====================

  async getStockReservations(organization, productId = null, warehouseId = null, status = null, reservationType = null) {
    const params = new URLSearchParams({ organization });
    if (productId) params.append('productId', productId);
    if (warehouseId) params.append('warehouseId', warehouseId);
    if (status) params.append('status', status);
    if (reservationType) params.append('type', reservationType);
    return apiRequest(`/stock-reservations?${params}`);
  }

  async createStockReservation(reservationData) {
    return apiRequest('/stock-reservations', {
      method: 'POST',
      body: reservationData,
    });
  }

  async updateStockReservation(reservationId, updates) {
    return apiRequest(`/stock-reservations/${reservationId}`, {
      method: 'PUT',
      body: updates,
    });
  }

  async releaseStockReservation(reservationId) {
    return apiRequest(`/stock-reservations/${reservationId}/release`, {
      method: 'POST',
    });
  }

  async deleteStockReservation(reservationId) {
    return apiRequest(`/stock-reservations/${reservationId}`, {
      method: 'DELETE',
    });
  }

  // ==================== ENHANCED INVENTORY - RETURN ORDER (RMA) ENDPOINTS ====================

  async getReturnOrders(organization, returnType = null, status = null) {
    const params = new URLSearchParams({ organization });
    if (returnType) params.append('type', returnType);
    if (status) params.append('status', status);
    return apiRequest(`/return-orders?${params}`);
  }

  async createReturnOrder(returnData) {
    return apiRequest('/return-orders', {
      method: 'POST',
      body: returnData,
    });
  }

  async updateReturnOrder(returnId, updates) {
    return apiRequest(`/return-orders/${returnId}`, {
      method: 'PUT',
      body: updates,
    });
  }

  async approveReturnOrder(returnId, approvedBy) {
    return apiRequest(`/return-orders/${returnId}/approve`, {
      method: 'POST',
      body: { approvedBy },
    });
  }

  async deleteReturnOrder(returnId) {
    return apiRequest(`/return-orders/${returnId}`, {
      method: 'DELETE',
    });
  }

  // ==================== ENHANCED INVENTORY - PRODUCT CATEGORY ENDPOINTS ====================

  async getProductCategories(organization, parentId = null, activeOnly = true) {
    const params = new URLSearchParams({ organization, activeOnly: activeOnly.toString() });
    if (parentId !== null) params.append('parentId', parentId);
    return apiRequest(`/product-categories?${params}`);
  }

  async createProductCategory(categoryData) {
    return apiRequest('/product-categories', {
      method: 'POST',
      body: categoryData,
    });
  }

  async updateProductCategory(categoryId, updates) {
    return apiRequest(`/product-categories/${categoryId}`, {
      method: 'PUT',
      body: updates,
    });
  }

  async deleteProductCategory(categoryId) {
    return apiRequest(`/product-categories/${categoryId}`, {
      method: 'DELETE',
    });
  }

  // ==================== ENHANCED INVENTORY - PRODUCT VARIANT ENDPOINTS ====================

  async getProductVariants(productId, activeOnly = true) {
    const params = new URLSearchParams({ productId: productId.toString(), activeOnly: activeOnly.toString() });
    return apiRequest(`/product-variants?${params}`);
  }

  async createProductVariant(variantData) {
    return apiRequest('/product-variants', {
      method: 'POST',
      body: variantData,
    });
  }

  async updateProductVariant(variantId, updates) {
    return apiRequest(`/product-variants/${variantId}`, {
      method: 'PUT',
      body: updates,
    });
  }

  async deleteProductVariant(variantId) {
    return apiRequest(`/product-variants/${variantId}`, {
      method: 'DELETE',
    });
  }

  // ==================== RESOURCE PARAM ENDPOINTS ====================

  async getResourceParams(resourceId) {
    return apiRequest(`/resources/${resourceId}/params`);
  }

  async createResourceParam(resourceId, paramData) {
    return apiRequest(`/resources/${resourceId}/params`, {
      method: 'POST',
      body: paramData,
    });
  }

  async updateResourceParam(resourceId, paramId, updates) {
    return apiRequest(`/resources/${resourceId}/params/${paramId}`, {
      method: 'PUT',
      body: updates,
    });
  }

  async deleteResourceParam(resourceId, paramId) {
    return apiRequest(`/resources/${resourceId}/params/${paramId}`, {
      method: 'DELETE',
    });
  }

  // ==================== RESOURCE TAG ENDPOINTS ====================

  async getResourceTags(resourceId) {
    return apiRequest(`/resources/${resourceId}/tags`);
  }

  async createResourceTag(resourceId, tagData) {
    return apiRequest(`/resources/${resourceId}/tags`, {
      method: 'POST',
      body: tagData,
    });
  }

  async updateResourceTag(resourceId, tagId, updates) {
    return apiRequest(`/resources/${resourceId}/tags/${tagId}`, {
      method: 'PUT',
      body: updates,
    });
  }

  async deleteResourceTag(resourceId, tagId) {
    return apiRequest(`/resources/${resourceId}/tags/${tagId}`, {
      method: 'DELETE',
    });
  }

  // ==================== PRODUCT PRICE LIST ENDPOINTS ====================

  async getProductPriceLists(productId) {
    return apiRequest(`/products/${productId}/price-lists`);
  }

  async createProductPriceList(productId, priceListData) {
    return apiRequest(`/products/${productId}/price-lists`, {
      method: 'POST',
      body: priceListData,
    });
  }

  async updateProductPriceList(productId, priceListId, updates) {
    return apiRequest(`/products/${productId}/price-lists/${priceListId}`, {
      method: 'PUT',
      body: updates,
    });
  }

  async deleteProductPriceList(productId, priceListId) {
    return apiRequest(`/products/${productId}/price-lists/${priceListId}`, {
      method: 'DELETE',
    });
  }

  // ==================== PRODUCT SUPPLIER ENDPOINTS ====================

  async getProductSuppliers(productId) {
    return apiRequest(`/products/${productId}/suppliers`);
  }

  async createProductSupplier(productId, supplierData) {
    return apiRequest(`/products/${productId}/suppliers`, {
      method: 'POST',
      body: supplierData,
    });
  }

  async updateProductSupplier(productId, supplierId, updates) {
    return apiRequest(`/products/${productId}/suppliers/${supplierId}`, {
      method: 'PUT',
      body: updates,
    });
  }

  async deleteProductSupplier(productId, supplierId) {
    return apiRequest(`/products/${productId}/suppliers/${supplierId}`, {
      method: 'DELETE',
    });
  }

  // ==================== PURCHASE ORDER ITEM ENDPOINTS ====================

  async getPurchaseOrderItems(poId) {
    return apiRequest(`/purchase-orders/${poId}/items`);
  }

  async createPurchaseOrderItem(poId, itemData) {
    return apiRequest(`/purchase-orders/${poId}/items`, {
      method: 'POST',
      body: itemData,
    });
  }

  async updatePurchaseOrderItem(poId, itemId, updates) {
    return apiRequest(`/purchase-orders/${poId}/items/${itemId}`, {
      method: 'PUT',
      body: updates,
    });
  }

  async deletePurchaseOrderItem(poId, itemId) {
    return apiRequest(`/purchase-orders/${poId}/items/${itemId}`, {
      method: 'DELETE',
    });
  }

  // ==================== STOCK TRANSFER ITEM ENDPOINTS ====================

  async getStockTransferItems(transferId) {
    return apiRequest(`/stock-transfers/${transferId}/items`);
  }

  async createStockTransferItem(transferId, itemData) {
    return apiRequest(`/stock-transfers/${transferId}/items`, {
      method: 'POST',
      body: itemData,
    });
  }

  async updateStockTransferItem(transferId, itemId, updates) {
    return apiRequest(`/stock-transfers/${transferId}/items/${itemId}`, {
      method: 'PUT',
      body: updates,
    });
  }

  async deleteStockTransferItem(transferId, itemId) {
    return apiRequest(`/stock-transfers/${transferId}/items/${itemId}`, {
      method: 'DELETE',
    });
  }

  // ==================== CYCLE COUNT ITEM ENDPOINTS ====================

  async getCycleCountItems(cycleCountId) {
    return apiRequest(`/cycle-counts/${cycleCountId}/items`);
  }

  async createCycleCountItem(cycleCountId, itemData) {
    return apiRequest(`/cycle-counts/${cycleCountId}/items`, {
      method: 'POST',
      body: itemData,
    });
  }

  async updateCycleCountItem(cycleCountId, itemId, updates) {
    return apiRequest(`/cycle-counts/${cycleCountId}/items/${itemId}`, {
      method: 'PUT',
      body: updates,
    });
  }

  async deleteCycleCountItem(cycleCountId, itemId) {
    return apiRequest(`/cycle-counts/${cycleCountId}/items/${itemId}`, {
      method: 'DELETE',
    });
  }

  // ==================== SALES ORDER ITEM ENDPOINTS ====================

  async getSalesOrderItems(orderId) {
    return apiRequest(`/sales-orders/${orderId}/items`);
  }

  async createSalesOrderItem(orderId, itemData) {
    return apiRequest(`/sales-orders/${orderId}/items`, {
      method: 'POST',
      body: itemData,
    });
  }

  async updateSalesOrderItem(orderId, itemId, updates) {
    return apiRequest(`/sales-orders/${orderId}/items/${itemId}`, {
      method: 'PUT',
      body: updates,
    });
  }

  async deleteSalesOrderItem(orderId, itemId) {
    return apiRequest(`/sales-orders/${orderId}/items/${itemId}`, {
      method: 'DELETE',
    });
  }

  // ==================== RETURN ORDER ITEM ENDPOINTS ====================

  async getReturnOrderItems(returnId) {
    return apiRequest(`/return-orders/${returnId}/items`);
  }

  async createReturnOrderItem(returnId, itemData) {
    return apiRequest(`/return-orders/${returnId}/items`, {
      method: 'POST',
      body: itemData,
    });
  }

  async updateReturnOrderItem(returnId, itemId, updates) {
    return apiRequest(`/return-orders/${returnId}/items/${itemId}`, {
      method: 'PUT',
      body: updates,
    });
  }

  async deleteReturnOrderItem(returnId, itemId) {
    return apiRequest(`/return-orders/${returnId}/items/${itemId}`, {
      method: 'DELETE',
    });
  }
}

const inventoryApi = new InventoryAPI();
export default inventoryApi;
